import { useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { getExtraHeaders } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Award, BookOpen, CheckCircle2, Compass, Medal, ShieldCheck, Sparkles } from "lucide-react";
import { formatIssuedDate } from "@/lib/studentCourses";

interface VerifiedCredential {
  title: string;
  courseTitle: string;
  emoji: string | null;
  issuedAt: string;
  studentName: string;
  lessonsCompleted: number;
  averageScore: number;
}

interface VerifyPayload {
  valid: boolean;
  credential?: VerifiedCredential;
}

/**
 * Public, shareable certificate page. No auth wrapper: a parent, teacher or friend
 * who has never signed in must be able to open this link and see the credential.
 */
export default function CredentialPage() {
  const { code } = useParams<{ code: string }>();
  const [, setLocation] = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [code]);

  const { data, isLoading } = useQuery<VerifyPayload>({
    queryKey: ["/api/credentials/verify", code],
    enabled: !!code,
    queryFn: async () => {
      const res = await fetch(`/api/credentials/verify/${encodeURIComponent(code!)}`, {
        credentials: "include",
        headers: getExtraHeaders(),
      });
      if (res.status === 404) return { valid: false };
      if (!res.ok) throw new Error("Could not check this certificate");
      return res.json();
    },
    retry: false,
  });

  const credential = data?.valid ? data.credential : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-amber-100/40 dark:to-amber-900/10 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-5">
        {isLoading ? (
          <Skeleton className="h-96 rounded-3xl" data-testid="skeleton-credential" />
        ) : !credential ? (
          <Card className="rounded-3xl border-dashed" data-testid="card-credential-invalid">
            <CardContent className="p-8 text-center space-y-4">
              <div className="text-5xl">🔎</div>
              <div>
                <h1 className="text-2xl font-extrabold">We could not find that certificate</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  The link may have a typo in it. Check the code and try again, or start a course of your own.
                </p>
              </div>
              <Button className="rounded-xl font-bold" onClick={() => setLocation("/courses")} data-testid="button-credential-start-learning">
                <Compass className="w-4 h-4 mr-2" /> Start learning
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card
              className="rounded-3xl border-4 border-amber-300 dark:border-amber-500/50 bg-card shadow-lg animate-in fade-in zoom-in-95 duration-500 fill-mode-both"
              data-testid="card-credential"
            >
              <CardContent className="p-6 sm:p-10 text-center space-y-5">
                <div className="flex items-center justify-center gap-2 text-primary">
                  <Sparkles className="w-5 h-5" />
                  <span className="text-lg sm:text-xl font-extrabold tracking-tight">EduFocal Academy</span>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent" />

                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Certificate of completion</p>
                  <div className="text-6xl" aria-hidden="true">{credential.emoji || "🏅"}</div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="text-credential-title">
                    {credential.title}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    for completing <span className="font-semibold text-foreground">{credential.courseTitle}</span>
                  </p>
                </div>

                <div className="py-2">
                  <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Awarded to</p>
                  <p className="text-3xl sm:text-4xl font-extrabold text-primary mt-1" data-testid="text-credential-student">
                    {credential.studentName}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
                  <div className="rounded-2xl bg-muted/50 p-3">
                    <BookOpen className="w-5 h-5 mx-auto text-primary mb-1" />
                    <p className="text-xl font-extrabold" data-testid="text-credential-lessons">{credential.lessonsCompleted}</p>
                    <p className="text-xs text-muted-foreground">Lessons completed</p>
                  </div>
                  <div className="rounded-2xl bg-muted/50 p-3">
                    <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
                    <p className="text-xl font-extrabold" data-testid="text-credential-score">{Number.isFinite(credential.averageScore) ? Math.round(credential.averageScore) : 0}%</p>
                    <p className="text-xs text-muted-foreground">Average score</p>
                  </div>
                  <div className="rounded-2xl bg-muted/50 p-3 col-span-2 sm:col-span-1">
                    <Award className="w-5 h-5 mx-auto text-amber-500 mb-1" />
                    <p className="text-sm font-extrabold" data-testid="text-credential-issued">{formatIssuedDate(credential.issuedAt)}</p>
                    <p className="text-xs text-muted-foreground">Date issued</p>
                  </div>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent" />

                <p className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  <ShieldCheck className="w-4 h-4" /> Verified by EduFocal Academy
                </p>
                <p className="text-xs text-muted-foreground">Certificate code: {code}</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-primary/30" data-testid="card-credential-cta">
              <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 text-center sm:text-left">
                <Medal className="w-10 h-10 text-amber-500 mx-auto sm:mx-0 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">Want a certificate like this one?</p>
                  <p className="text-sm text-muted-foreground">
                    Pick a course you love, play the lessons and earn your own.
                  </p>
                </div>
                <Button className="rounded-xl font-bold flex-shrink-0" onClick={() => setLocation("/courses")} data-testid="button-credential-start-learning">
                  <Compass className="w-4 h-4 mr-2" /> Start learning
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
